import CustomBackButton from "@/components/custom/custom-back-button";
import { CustomButton } from "@/components/custom/custom-button";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { useBusinessKyc } from "@/hooks/use-business-kyc";
import KycBusinessAccount from "@/pages/users/customers/_components/kyc-business-account";
import KycBusinessInfo from "@/pages/users/customers/_components/kyc-business-info";
import KycContact from "@/pages/users/customers/_components/kyc-contact";
import KycDirectorInfo from "@/pages/users/customers/_components/kyc-director-info";
import KycDocuments from "@/pages/users/customers/_components/kyc-documents";
import KycOnline from "@/pages/users/customers/_components/kyc-online";
import { approveKYC, rejectKYC } from "@/services/kyc.service";
import { formatLabel } from "@/utils/format-label";
import { usePermission } from "@/hooks/use-permissions";
import { CAPABILITIES } from "@/config/permissions";

import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";
import CustomReasonDialog from "@/components/custom/custom-reason-dialog";
import { kycRejectSchema } from "@/schemas/kyc.schema";

const SectionRoot = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const KycDetails = () => {
  const canDecide = usePermission(CAPABILITIES.KYC_DECIDE);
  const queryClient = useQueryClient();
  const { merchantId } = useParams();

  const dispatch = useDispatch();


  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const {
    data,
    isLoading,
    isError: isKycError,
    error,
  } = useBusinessKyc(merchantId, !!merchantId);

  const approveMutation = useMutation({
    mutationFn: () => approveKYC(merchantId),
    onSuccess: async (response) => {
      setApproveOpen(false);

      dispatch(showToast(response?.message ?? "KYC Approved", "success"));

      await queryClient.invalidateQueries({
        queryKey: ["business-kyc", merchantId],
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason) => rejectKYC(merchantId, reason),
    onSuccess: async (response) => {
      setRejectOpen(false);

      dispatch(showToast(response?.message ?? "KYC Rejected", "success"));

      await queryClient.invalidateQueries({
        queryKey: ["business-kyc", merchantId],
      });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading KYC details...
        </Typography>
      </Box>
    );
  }

  const approvalStatus = data?.approvalStatus;

  const isSubmitted = approvalStatus === "SUBMITTED";
  // const isApproved = approvalStatus === "APPROVED";
  // const isRejected = approvalStatus === "REJECTED";

  const isUpdating = approveMutation.isPending || rejectMutation.isPending;

  const actionsDisabled = !canDecide || !isSubmitted || isUpdating || isKycError || !data;

  const statusStyleMap = {
    APPROVED: { bg: "#ecfdf5", color: "#065f46", dot: "#10b981" },
    PENDING: { bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" },
    REJECTED: { bg: "#fef2f2", color: "#991b1b", dot: "#ef4444" },
    DRAFT: { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" },
    SUBMITTED: { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" },
  };

  const statusStyle = statusStyleMap[approvalStatus] ?? statusStyleMap.DRAFT;

  return (
    <SectionRoot>
      <Stack direction="row" alignItems="center" spacing={2}>
        <CustomBackButton />

        <Typography variant="h6" fontWeight={500}>
          KYC Information
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: "999px",
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            fontSize: "0.78rem",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: statusStyle.dot,
            }}
          />
          {formatLabel(approvalStatus ?? "UNKNOWN")}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1}>
          {isSubmitted && canDecide && (
            <CustomButton
              variantType="outline"
              sx={{ height: 35 }}
              disabled={actionsDisabled}
              onClick={() => setApproveOpen(true)}
            >
              Approve
            </CustomButton>
          )}

          {isSubmitted && canDecide && (
            <CustomButton
              variantType="destructive"
              sx={{ height: 35 }}
              disabled={actionsDisabled}
              onClick={() => setRejectOpen(true)}
            >
              Reject
            </CustomButton>
          )}
        </Stack>
      </Stack>

      <Card>
        {isKycError ? (
          <CustomErrorMessage
          message={
            error?.statusCode === 403
              ? "You do not have permission to view this merchant's KYC."
              : "KYC information not found for this merchant."
          }
        />
        ) : (
          <>
            <KycBusinessInfo d={data} />
            <Divider />
            <KycContact d={data} />
            <Divider />
            <KycOnline />
            <Divider />
            <KycBusinessAccount d={data} />
            <Divider />
            <KycDocuments d={data} />
          </>
        )}

        <Divider />

        <KycDirectorInfo directors={data?.directors ?? []} />
      </Card>

      <CustomConfirmDialog
        open={approveOpen && isSubmitted}
        onClose={() => setApproveOpen(false)}
        title="Approve KYC Application"
        description="Are you sure you want to approve this merchant's KYC? This action cannot be undone."
        confirmLabel="Approve"
        variant="warning"
        mutation={approveMutation}
        onConfirm={() => approveMutation.mutate()}
      />

      <CustomReasonDialog
        open={rejectOpen && isSubmitted}
        onClose={() => setRejectOpen(false)}
        title="Reject KYC Application"
        description="This action will reject the merchant's KYC submission."
        label="Reason for rejection"
        placeholder="e.g. Submitted documents are unclear or incomplete..."
        confirmLabel="Reject KYC"
        pendingLabel="Rejecting..."
        variant="danger"
        schema={kycRejectSchema}
        mutation={rejectMutation}
        onConfirm={(reason) => rejectMutation.mutate(reason)}
      />
    </SectionRoot>
  );
};

export default KycDetails;
